import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PILLAR_LABELS } from '../lib/pillars';
import { 
  Activity, LayoutDashboard, Dumbbell, PlayCircle,
  Heart, Settings, LogOut, X, Watch, Video, Film,
  MessageCircle, Utensils, Brain, Moon, ChevronRight, ChevronDown, Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import MobileTopBar from './mobile/MobileTopBar';
import MobileBottomNav from './mobile/MobileBottomNav';
import MobileMoreSheet from './mobile/MobileMoreSheet';
import { MilestoneCelebrations } from './MilestoneCelebrations';

/**
 * Sidebar layout.
 *
 * Three groups, separated by dividers:
 *   1. Core           — Chat, Dashboard
 *   2. Wellness IQ    — Movement (expandable: Workouts / Sessions / Mobility),
 *                       Recovery, Mental, Cycle, Nutrition
 *   3. Tools          — Movement Analyser, Live Sessions, Wearables
 *
 * Each pillar item has its own accent color (lime / cyan / purple / pink /
 * orange). The Movement parent auto-expands when the user is on any child
 * page so the active item stays visible.
 */

const PILLAR_COLOR = {
  movement:  '#CCFF00',
  recovery:  '#22d3ee',
  mental:    '#a855f7',
  cycle:     '#ec4899',
  nutrition: '#fb923c',
};

const MOVEMENT_CHILDREN = ['/performance', '/workouts', '/sessions', '/mobility'];

const NavItem = ({ to, icon, label, active, accentColor, indent, testId }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${indent ? 'pl-10' : ''} ${
      active
        ? 'bg-zinc-800/60 text-white'
        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/40'
    }`}
    style={active && accentColor ? { borderLeft: `2px solid ${accentColor}`, color: accentColor } : undefined}
    data-testid={testId}
  >
    {icon}
    <span className="font-secondary tracking-wide text-sm">{label}</span>
  </Link>
);

const SectionDivider = ({ label }) => (
  <div className="px-3 pt-4 pb-1 flex items-center gap-2">
    <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 font-secondary font-bold">{label}</span>
    <div className="flex-1 h-px bg-zinc-800" />
  </div>
);

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isCoach = user?.role === 'coach';
  const isOnMovement = MOVEMENT_CHILDREN.includes(location.pathname);
  const [movementOpen, setMovementOpen] = useState(isOnMovement);

  // Auto-expand Movement whenever the user lands on any of its children.
  // Synced during render (React's recommended pattern) rather than in an
  // effect — still user-toggleable via the chevron.
  const [prevOnMovement, setPrevOnMovement] = useState(isOnMovement);
  if (isOnMovement !== prevOnMovement) {
    setPrevOnMovement(isOnMovement);
    if (isOnMovement) setMovementOpen(true);
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      <MilestoneCelebrations />
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-zinc-800 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800 flex-shrink-0 safe-top">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
              <Activity className="w-5 h-5 text-black" />
            </div>
            <span className="font-secondary font-bold text-xl tracking-tight">Revivo<span className="text-primary">&nbsp;IQ</span></span>
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation — scrollable so the User Profile footer never overlaps */}
        <nav className="px-3 py-3 space-y-0.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 4rem - 7rem)' }}>
          {isCoach ? (
            <>
              <NavItem to="/chat" icon={<MessageCircle className="w-5 h-5" />} label="Chat" active={isActive('/chat')} testId="nav-chat" />
              <NavItem to="/dashboard" icon={<LayoutDashboard className="w-5 h-5" />} label="Dashboard" active={isActive('/dashboard')} testId="nav-dashboard" />
              <NavItem to="/coach" icon={<Dumbbell className="w-5 h-5" />} label="Create Workout" active={isActive('/coach')} testId="nav-create-workout" />
              <NavItem to="/live-sessions" icon={<Video className="w-5 h-5" />} label="Live Sessions" active={isActive('/live-sessions')} testId="nav-live-sessions" />
              <NavItem to="/performance" icon={<Activity className="w-5 h-5" />} label={PILLAR_LABELS.movement} active={isActive('/performance')} testId="nav-movement" />
            </>
          ) : (
            <>
              <NavItem to="/chat" icon={<MessageCircle className="w-5 h-5" />} label="Chat" active={isActive('/chat')} testId="nav-chat" />
              <NavItem to="/dashboard" icon={<LayoutDashboard className="w-5 h-5" />} label="Dashboard" active={isActive('/dashboard')} testId="nav-dashboard" />
              <NavItem to="/journey" icon={<Trophy className="w-5 h-5" />} label="Journey" active={isActive('/journey')} accentColor="#CCFF00" testId="nav-journey" />

              <SectionDivider label="Wellness IQ" />

              {/* Movement parent — clicking the row navigates to /performance
                  (the Movement deep-dive). Clicking the chevron alone just
                  toggles expand. Children are indented and auto-shown when
                  the user is on any of them. */}
              <div className="flex items-stretch group" data-testid="nav-movement-group">
                <Link
                  to="/performance"
                  onClick={() => setMovementOpen(true)}
                  className={`flex-1 flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                    isOnMovement ? 'bg-zinc-800/60' : 'hover:bg-zinc-800/40'
                  }`}
                  style={isOnMovement ? { borderLeft: `2px solid ${PILLAR_COLOR.movement}`, color: PILLAR_COLOR.movement } : { color: '#a1a1aa' }}
                  data-testid="nav-movement"
                >
                  <Activity className="w-5 h-5" />
                  <span className="font-secondary tracking-wide text-sm">{PILLAR_LABELS.movement}</span>
                </Link>
                <button
                  onClick={(e) => { e.preventDefault(); setMovementOpen(o => !o); }}
                  className="px-2 text-zinc-500 hover:text-white"
                  aria-label={movementOpen ? `Collapse ${PILLAR_LABELS.movement}` : `Expand ${PILLAR_LABELS.movement}`}
                  data-testid="nav-movement-toggle"
                >
                  {movementOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              </div>
              {movementOpen && (
                <div className="space-y-0.5" data-testid="nav-movement-children">
                  <NavItem to="/workouts" icon={<Dumbbell className="w-4 h-4" />} label="Workouts" active={isActive('/workouts')} accentColor={PILLAR_COLOR.movement} indent testId="nav-workouts" />
                  <NavItem to="/sessions" icon={<PlayCircle className="w-4 h-4" />} label="Sessions" active={isActive('/sessions')} accentColor={PILLAR_COLOR.movement} indent testId="nav-sessions" />
                  <NavItem to="/mobility" icon={<Activity className="w-4 h-4" />} label="Mobility" active={isActive('/mobility')} accentColor={PILLAR_COLOR.movement} indent testId="nav-mobility" />
                </div>
              )}

              <NavItem to="/recovery" icon={<Heart className="w-5 h-5" />} label={PILLAR_LABELS.recovery} active={isActive('/recovery')} accentColor={PILLAR_COLOR.recovery} testId="nav-recovery" />
              <NavItem to="/mental" icon={<Brain className="w-5 h-5" />} label={PILLAR_LABELS.mental} active={isActive('/mental')} accentColor={PILLAR_COLOR.mental} testId="nav-mental" />
              <NavItem to="/cycle" icon={<Moon className="w-5 h-5" />} label="Cycle" active={isActive('/cycle')} accentColor={PILLAR_COLOR.cycle} testId="nav-cycle" />
              <NavItem to="/nutrition" icon={<Utensils className="w-5 h-5" />} label={PILLAR_LABELS.nutrition} active={isActive('/nutrition')} accentColor={PILLAR_COLOR.nutrition} testId="nav-nutrition" />

              <SectionDivider label="Tools" />

              <NavItem to="/analyze" icon={<Film className="w-5 h-5" />} label="Movement Analyser" active={isActive('/analyze')} testId="nav-movement-analyser" />
              <NavItem to="/live-sessions" icon={<Video className="w-5 h-5" />} label="Live Sessions" active={isActive('/live-sessions')} testId="nav-live-sessions" />
              <NavItem to="/wearables" icon={<Watch className="w-5 h-5" />} label="Wearables" active={isActive('/wearables')} testId="nav-wearables" />
            </>
          )}
        </nav>

        {/* User Profile — fixed footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800 bg-surface safe-bottom">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
              <span className="text-sm font-bold">{user?.name?.charAt(0) || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-zinc-500 uppercase">{user?.role || 'Athlete'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/profile" className="flex-1">
              <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white" size="sm" data-testid="nav-settings">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="text-zinc-400 hover:text-red-400" 
              size="sm"
              onClick={handleLogout}
              data-testid="nav-logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 lg:ml-64">
        {/* Mobile top bar — wordmark + Journey level/XP + "You" sheet trigger */}
        <MobileTopBar onYouClick={() => setMoreOpen(true)} />

        {/* Page Content — padding for the fixed top bar (mobile) and the fixed
            bottom tab bar so neither covers content. */}
        <main className="min-h-screen pt-[calc(env(safe-area-inset-top)+4.625rem)] lg:pt-0 pb-24 lg:pb-0">
          {children}
        </main>
      </div>

      {/* Mobile app-style navigation */}
      <MobileBottomNav />
      <MobileMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
